import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Quotes, Star } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FALLBACK = [
  {
    review_id: 'fb-1',
    name: 'A novelist · early reader',
    rating: 5,
    text: 'It caught three voice slips my editor missed — and didn\'t touch a single line of dialogue. That\'s the trick, isn\'t it?',
    project_type: 'Novel',
    location: null,
  },
];

const Stars = ({ rating }) => (
  <div className="inline-flex items-center gap-0.5 mb-3">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star
        key={n}
        size={14}
        weight={n <= rating ? 'fill' : 'regular'}
        className={n <= rating ? 'text-gold' : 'text-ash/40'}
      />
    ))}
  </div>
);

export const WhatWritersAreSaying = () => {
  const [reviews, setReviews] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/reviews`)
      .then((r) => { if (!cancelled) setReviews(r.data?.reviews || []); })
      .catch(() => { if (!cancelled) setReviews([]); });
    return () => { cancelled = true; };
  }, []);

  if (reviews === null) {
    return (
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="h-40 animate-pulse rounded-lg bg-stone/30" />
      </section>
    );
  }

  const items = reviews.length > 0 ? reviews : FALLBACK;
  const isSingle = items.length === 1;

  return (
    <section className="max-w-6xl mx-auto px-6 md:px-12 py-24 md:py-32" data-testid="what-writers-saying-section">
      <div className="text-center mb-14">
        <p className="text-[10px] font-medium uppercase tracking-[0.32em] text-gold mb-3">Reader response</p>
        <h2 className="text-3xl md:text-5xl font-serif font-light tracking-tight text-ivory leading-[1.15]">
          What <em className="text-gold not-italic font-normal">writers</em> are saying
        </h2>
      </div>

      {isSingle ? (
        <div className="max-w-3xl mx-auto text-center">
          <Quotes size={28} weight="duotone" className="text-gold mx-auto mb-6" />
          <Stars rating={items[0].rating || 5} />
          <blockquote className="text-2xl md:text-3xl font-serif font-light tracking-tight text-ivory leading-[1.3] mb-6">
            "{items[0].text}"
          </blockquote>
          <p className="text-xs font-medium uppercase tracking-[0.28em] text-ash">
            {items[0].name}
            {items[0].project_type ? ` · ${items[0].project_type}` : ''}
            {items[0].location ? ` · ${items[0].location}` : ''}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {items.slice(0, 9).map((r) => (
            <article
              key={r.review_id}
              className="panel-luxe p-7 md:p-8 flex flex-col"
              data-testid={`public-review-${r.review_id}`}
            >
              <Quotes size={20} weight="duotone" className="text-gold mb-4" />
              <Stars rating={r.rating || 5} />
              <p className="text-base text-ivory font-serif font-light leading-[1.6] flex-1">"{r.text}"</p>
              <div className="mt-6 pt-5 border-t border-border/60">
                <p className="text-sm font-medium text-ivory">{r.name}</p>
                <p className="text-[10px] font-medium uppercase tracking-[0.28em] text-ash mt-1">
                  {[r.project_type, r.location].filter(Boolean).join(' · ') || ' '}
                </p>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
};
